'use client';

import {
  TelegramAvailableFeaturesControllerFindAll200,
  TelegramTransactionsUserControllerGetActivePaymentSystems200,
  TelegramTransactionsUserControllerGetTopUpVariants200,
  useTelegramAvailableFeaturesFindAll,
  useTelegramTransactionsUserGetActivePaymentSystems,
  useTelegramTransactionsUserGetTopUpVariants,
} from '@/generated';
import { Loading } from '../common/Screens/Loading';
import { createFeaturePurchaseSubflow, featuresForType } from './state';
import { useCallback, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { TopUp } from './Screens/TopUp';
import { PremiumComponent } from './Screens/Premium';
import { PaymentSummary } from './Screens/Summary';
import { useUserData } from '@/contexts/LoginContext';
import { useModals } from '@/contexts/ModalContext';
import { AfterPurchaseWithCoin } from '@/modals/AfterPurchaseWithCoinsMade';
import { FinalScreen } from '../common/Screens/FinalScreen';
import { FeatureCheckout } from './Checkout';
import type { FeatureCheckoutFlowActor } from './checkoutFlow';
import { runtimeConfig } from '@/config/runtime-config';
import { filterPaymentSystemsForRuntime } from '@/utils/payment-systems';
import { useDelayedFlag } from '@/hooks/useDelayedFlag.hook';

type Props = {
  productId: string;
  onClose: () => void;
  mode?: 'topup' | 'vip';
};
export function MonetizationWidget({ productId, onClose, mode }: Props) {
  const { data: features, isLoading: featuresLoading } = useTelegramAvailableFeaturesFindAll();
  const { data: payments, isLoading: paymentsLoading } = useTelegramTransactionsUserGetActivePaymentSystems();
  const { data: availableAmounts, isLoading: amountsLoading } = useTelegramTransactionsUserGetTopUpVariants();

  const me = useUserData();
  const filteredPayments = filterPaymentSystemsForRuntime(payments, runtimeConfig.isTelegramMiniApp);

  const isLoading = featuresLoading || paymentsLoading || amountsLoading;
  const showLoading = useDelayedFlag(isLoading);

  return (
    <>
      {isLoading ? (
        showLoading && <Loading onClose={onClose} />
      ) : (
        <MonetizationPage
          onClose={onClose}
          coinsBalance={me?.balance || 0}
          productId={productId}
          availableAmounts={availableAmounts || []}
          features={features || []}
          payments={filteredPayments}
          mode={mode}
        />
      )}
    </>
  );
}

type InnerProps = {
  onClose: () => void;
  productId: string;
  features: TelegramAvailableFeaturesControllerFindAll200;
  payments: TelegramTransactionsUserControllerGetActivePaymentSystems200;
  availableAmounts: TelegramTransactionsUserControllerGetTopUpVariants200;
  coinsBalance: number;
  mode?: 'topup' | 'vip';
};

export const MonetizationPage: React.FC<InnerProps> = ({
  onClose,
  productId,
  availableAmounts,
  coinsBalance,
  payments,
  features,
  mode,
}) => {
  const { open, close } = useModals();

  const userData = useUserData();

  const onSuccessPay = useCallback(
    (productId: string) => {
      onClose();
      open(
        'after-coin-bought-modal',
        <AfterPurchaseWithCoin
          productId={productId}
          onClose={() => {
            close('after-coin-bought-modal');
          }}
        />,
        true,
        'md:h-unset h-unset absolute md:static ',
      );
    },
    [open, close, onClose],
  );

  const machine = useMemo(() => createFeaturePurchaseSubflow(onSuccessPay), [onSuccessPay]);

  const typesOrder = mode === 'topup' ? ['TopUp'] : mode === 'vip' ? ['Premium'] : ['TopUp', 'Premium'];
  const initialSelected = mode === 'vip' ? features.filter((f) => f.type === 'Premium').map((f) => f.id) : undefined;
  const skipSummary = !!mode;

  const [state, send] = useMachine(machine, {
    input: { features, payments, availableAmounts, coinsBalance, productId, typesOrder, initialSelected, skipSummary },
  });

  const renderStep = useCallback(() => {
    if (state.matches('selecting')) {
      const feature = featuresForType(state.context.features, state.context.typesOrder[state.context.typeIndex]);
      if (feature.type === 'TopUp') {
        const haveSelected = state.context.selected.find((val) => state.context.dict[val].type === 'TopUp');

        return (
          <TopUp
            onGoBack={onClose}
            available={feature}
            onClose={onClose}
            onSelect={(id: string) => send({ type: 'TOGGLE_FEATURE', id })}
            onGoNext={() => send({ type: 'NEXT_PAGE' })}
            selected={state.context.selected}
            disabledNext={!haveSelected}
          />
        );
      }
      if (feature.type === 'Premium') {
        return (
          <PremiumComponent
            selectedIds={state.context.selected}
            available={feature}
            onSelect={(id: string) => send({ type: 'TOGGLE_FEATURE', id })}
            onGoNext={() => send({ type: 'NEXT_PAGE' })}
            onGoBack={() => send({ type: 'PREV_PAGE' })}
            onClose={onClose}
            onDeselect={(id: string) => send({ type: 'TOGGLE_FEATURE', id })}
            hideSwitch={mode === 'vip'}
          />
        );
      }
    }

    if (state.matches('summary')) {
      const selectedServices = state.context.selected.map((val) => state.context.dict[val]);
      return (
        <PaymentSummary
          onClose={onClose}
          onGoBack={() => send({ type: 'PREV_PAGE' })}
          selectedServices={selectedServices}
          onGoNext={() => send({ type: 'NEXT_PAGE' })}
          fiatCost={state.context.fiatCost}
          fiatFakeCost={state.context.fiatFakeCost}
        />
      );
    }

    if (state.matches('checkout')) {
      const checkoutActor = (state.children as any)['featureCheckoutFlow'];

      if (!checkoutActor) {
        return <Loading onClose={onClose} />;
      }

      return (
        <FeatureCheckout
          actor={checkoutActor as FeatureCheckoutFlowActor}
          onClose={onClose}
          onBackToSummary={() => send({ type: 'BACK_TO_SUMMARY' })}
        />
      );
    }

    if (state.matches('thankYou')) {
      return (
        <FinalScreen
          productId={state.context?.productId}
          resultBalance={String(userData?.balance) || '0'}
          amountPurchased={'0'}
          paidAmount={
            runtimeConfig.isTelegramMiniApp && state.context.paidPaymentSystem === 'TELEGRAM_STARS'
              ? state.context.telegramStarsAmount
              : undefined
          }
          paidCurrencySign={
            runtimeConfig.isTelegramMiniApp && state.context.paidPaymentSystem === 'TELEGRAM_STARS' ? '⭐' : undefined
          }
          onClose={onClose}
        />
      );
    }

    return null;
  }, [mode, onClose, send, state, userData]);

  return <>{renderStep()}</>;
};
